// SimpleMarquee.tsx
import { RefObject, useRef } from "react";
import styled from "styled-components";
import {
  motion,
  SpringOptions,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "motion/react";

// Custom wrap function
const wrap = (min: number, max: number, value: number): number => {
  const range = max - min;
  return ((((value - min) % range) + range) % range) + min;
};

export interface SimpleMarqueeProps {
  children: React.ReactNode;
  className?: string;
  direction?: "left" | "right" | "up" | "down";
  baseVelocity?: number;
  easing?: (value: number) => number;
  slowdownOnHover?: boolean;
  slowDownFactor?: number;
  slowDownSpringConfig?: SpringOptions;
  useScrollVelocity?: boolean;
  scrollAwareDirection?: boolean;
  scrollSpringConfig?: SpringOptions;
  scrollContainer?: RefObject<HTMLElement> | HTMLElement | null;
  repeat?: number;
  draggable?: boolean;
  dragSensitivity?: number;
  dragVelocityDecay?: number;
  dragAwareDirection?: boolean;
  dragAngle?: number;
  grabCursor?: boolean;
}

// Styled components
const Container = styled(motion.div)<{ direction: "horizontal" | "vertical" }>`
  display: flex;
  overflow: hidden;
  flex-direction: ${(props) => (props.direction === "horizontal" ? "row" : "column")};
`;

const Item = styled(motion.div)<{ isHorizontal: boolean; grabCursor: boolean }>`
  flex-shrink: 0;
  ${(props) => props.isHorizontal && `display: flex;`}
  ${(props) => props.grabCursor && `cursor: grab;`}
`;

const SimpleMarquee = ({
  children,
  className = "",
  direction = "right",
  baseVelocity = 5,
  slowdownOnHover = false,
  slowDownFactor = 0.3,
  slowDownSpringConfig = { damping: 50, stiffness: 400 },
  useScrollVelocity = false,
  scrollAwareDirection = false,
  scrollSpringConfig = { damping: 50, stiffness: 400 },
  scrollContainer,
  repeat = 3,
  draggable = false,
  dragSensitivity = 0.2,
  dragVelocityDecay = 0.96,
  dragAwareDirection = false,
  dragAngle = 0,
  grabCursor = false,
  easing,
}: SimpleMarqueeProps) => {
  const innerContainer = useRef<HTMLDivElement>(null);
  const baseX = useMotionValue(0);
  const baseY = useMotionValue(0);

  const { scrollY } = useScroll({
    container: scrollContainer && "current" in scrollContainer ? scrollContainer : innerContainer,
  });
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, scrollSpringConfig);

  const hoverFactorValue = useMotionValue(1);
  const defaultVelocity = useMotionValue(1);
  const isDragging = useRef(false);
  const dragVelocityRef = useRef(0);
  const smoothHoverFactor = useSpring(hoverFactorValue, slowDownSpringConfig);

  const velocityFactor = useTransform(useScrollVelocity ? smoothVelocity : defaultVelocity, [0, 1000], [0, 5], {
    clamp: false,
  });

  const isHorizontal = direction === "left" || direction === "right";
  const actualBaseVelocity = direction === "left" || direction === "up" ? -baseVelocity : baseVelocity;
  const isHovered = useRef(false);
  const directionFactor = useRef(1);

  const x = useTransform(baseX, (v) => {
    const wrapped = wrap(0, -100, v);
    return `${easing ? easing(wrapped / -100) * -100 : wrapped}%`;
  });
  const y = useTransform(baseY, (v) => {
    const wrapped = wrap(0, -100, v);
    return `${easing ? easing(wrapped / -100) * -100 : wrapped}%`;
  });

  useAnimationFrame((t, delta) => {
    if (isDragging.current && draggable) {
      if (isHorizontal) baseX.set(baseX.get() + dragVelocityRef.current);
      else baseY.set(baseY.get() + dragVelocityRef.current);

      dragVelocityRef.current *= 0.9;
      if (Math.abs(dragVelocityRef.current) < 0.01) dragVelocityRef.current = 0;
      return;
    }

    hoverFactorValue.set(isHovered.current && slowdownOnHover ? slowDownFactor : 1);

    let moveBy = directionFactor.current * actualBaseVelocity * (delta / 1000) * smoothHoverFactor.get();

    if (scrollAwareDirection && !isDragging.current) {
      const vel = velocityFactor.get();
      directionFactor.current = vel < 0 ? -1 : vel > 0 ? 1 : directionFactor.current;
    }

    moveBy += directionFactor.current * moveBy * velocityFactor.get();

    if (draggable) {
      moveBy += dragVelocityRef.current;
      if (dragAwareDirection && Math.abs(dragVelocityRef.current) > 0.1) {
        directionFactor.current = Math.sign(dragVelocityRef.current);
      }
      if (!isDragging.current && Math.abs(dragVelocityRef.current) > 0.01) {
        dragVelocityRef.current *= dragVelocityDecay;
      } else if (!isDragging.current) {
        dragVelocityRef.current = 0;
      }
    }

    if (isHorizontal) baseX.set(baseX.get() + moveBy);
    else baseY.set(baseY.get() + moveBy);
  });

  const lastPointer = useRef({ x: 0, y: 0 });
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!draggable) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (grabCursor) (e.currentTarget as HTMLElement).style.cursor = "grabbing";
    isDragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    dragVelocityRef.current = 0;
  };
  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggable || !isDragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    const rad = (dragAngle * Math.PI) / 180;
    const proj = dx * Math.cos(rad) + dy * Math.sin(rad);
    dragVelocityRef.current = proj * dragSensitivity;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };
  const handlePointerUp = (e: React.PointerEvent) => {
    if (!draggable) return;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    isDragging.current = false;
  };

  return (
    <Container
      direction={isHorizontal ? "horizontal" : "vertical"}
      className={className}
      onHoverStart={() => (isHovered.current = true)}
      onHoverEnd={() => (isHovered.current = false)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      ref={innerContainer}>
      {Array.from({ length: repeat }, (_, i) => i).map((i) => (
        <Item
          key={i}
          isHorizontal={isHorizontal}
          grabCursor={draggable && grabCursor}
          style={isHorizontal ? { x } : { y }}
          aria-hidden={i > 0}>
          {children}
        </Item>
      ))}
    </Container>
  );
};

export default SimpleMarquee;
